import { css } from "@emotion/react";
import { Box, TextField } from "@mui/material";
import { FontFamily, FontWeight } from "@unit/styles";
import React, { useRef } from "react";

type OTPInputProps = {
  length?: number;
  value: string;
  onChange: (value: string) => void;
};

const OTPInput: React.FC<OTPInputProps> = ({ length = 6, value, onChange }) => {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (index: number, v: string) => {
    if (!/^\d*$/.test(v)) {
      return;
    }
    let newValue = value.split("");
    if (v.length > 1) {
      v = v.charAt(v.length - 1);
    }
    newValue[index] = v;

    for (let i = 0; i < length; i++) {
      if (!newValue[i]) newValue[i] = "";
    }
    onChange(newValue.join(""));
    if (v && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (/^\d$/.test(e.key) && value[index]) {
      let newValue = value.split("");
      newValue[index] = e.key;
      for (let i = 0; i < length; i++) {
        if (!newValue[i]) newValue[i] = "";
      }
      onChange(newValue.join(""));
      if (index < length - 1) {
        inputsRef.current[index + 1]?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("Text").slice(0, length);
    const split = pastedData.split("");
    let newValue = Array(length).fill("");
    split.forEach((char, i) => {
      if (i < length && /^\d$/.test(char)) {
        newValue[i] = char;
      }
    });
    onChange(newValue.join(""));
    if (inputsRef.current[split.length - 1]) {
      inputsRef.current[split.length - 1]?.focus();
    }
  };

  return (
    <Box display="flex" gap={1} justifyContent="center" onPaste={handlePaste}>
      {Array.from({ length: length }).map((_, index) => (
        <Box key={index} css={style.squareWrapper}>
          <TextField
            variant="outlined"
            value={value[index] || ""}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onFocus={(e) => {
              const valueLength = e.target.value.length;
              e.target.setSelectionRange(valueLength, valueLength);
            }}
            inputProps={{
              maxLength: 1,
              onClick: (e) => {
                const input = e.currentTarget;
                input.setSelectionRange(input.value.length, input.value.length);
              },
            }}
            inputRef={(el) => (inputsRef.current[index] = el)}
            css={style.input}
          />{" "}
        </Box>
      ))}
    </Box>
  );
};

export default OTPInput;

const style = {
  squareWrapper: css`
    flex: 1;
    aspect-ratio: 1 / 1;
    max-width: 56px;
    min-width: 35px;
    max-height: 56px;
    min-height: 35px;
  `,
  input: css`
    width: 100%;
    height: 100%;

    input {
      height: 100%;
      aspect-ratio: 1 / 1;
      font-family: ${FontFamily.kintoSans};
      font-weight: ${FontWeight.medium};
      color: #0e201b;
      font-size: 18px;
      line-height: 1;
      padding: 0px;
      display: flex;
      justify-items: center;
    }

    fieldset {
      border-radius: 16px;
    }
  `,
};
